import { Link } from "react-router-dom";
import { COMPANY_INFO } from "../../../mocks/aegis-data";

export interface ServiceItem {
  id: number;
  icon: string;
  title: string;
  description: string;
  link: string;
}

interface ServicesSectionProps {
  title: string;
  subtitle: string;
  services: ServiceItem[];
  ctaText?: string;
  ctaHref?: string;
}

const SERVICE_IMAGES = [
  "https://public.readdy.ai/ai/img_res/4c7d6f34caab0ff4b11434e3bd3d8f83.jpg",
  "https://readdy.ai/api/search-image?query=luxury%20kitchen%20bathroom%20renovation%20interior%20Sarasota%20Florida%20high-end%20marble%20countertop%20professional%20remodel%20dramatic%20lighting%20editorial%20warm%20tones%20premium%20residential&width=800&height=600&seq=svc_img_02&orientation=landscape",
  "https://readdy.ai/api/search-image?query=concrete%20block%20masonry%20shell%20construction%20Florida%20residential%20framing%20structure%20building%20process%20editorial%20dramatic%20lighting%20professional&width=800&height=600&seq=svc_img_03&orientation=landscape",
  "https://readdy.ai/api/search-image?query=coastal%20construction%20Florida%20barrier%20island%20flood%20zone%20elevated%20home%20pilings%20ocean%20view%20dramatic%20aerial%20editorial%20professional%20premium%20residential%20warm%20light&width=800&height=600&seq=svc_img_04&orientation=landscape",
  "https://readdy.ai/api/search-image?query=FEMA%20elevation%20hurricane%20damage%20repair%20structural%20work%20Florida%20residential%20home%20foundation%20construction%20editorial%20warm%20professional&width=800&height=600&seq=svc_img_05&orientation=landscape",
  "https://readdy.ai/api/search-image?query=helical%20piles%20foundation%20work%20deep%20foundation%20installation%20residential%20construction%20Florida%20editorial%20dramatic%20professional%20warm%20lighting&width=800&height=600&seq=svc_img_06&orientation=landscape",
  "https://readdy.ai/api/search-image?query=licensed%20plumbing%20new%20construction%20residential%20home%20Florida%20pipes%20installation%20professional%20contractor%20editorial%20warm%20tones%20clean%20composition&width=800&height=600&seq=svc_img_07&orientation=landscape",
];

const NUM_LABELS = ["01", "02", "03", "04", "05", "06", "07"];

export default function ServicesSection({ title, subtitle, services, ctaText, ctaHref }: ServicesSectionProps) {
  return (
    <section className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-16">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-px bg-aegisGold" />
              <span className="font-inter text-xs tracking-[0.2em] uppercase text-aegisGold">Our Services</span>
            </div>
            <h2 className="font-cormorant font-semibold text-aegisDark leading-[1.1]" style={{ fontSize: "clamp(2.2rem, 3.5vw, 3.4rem)" }}>
              {title}
            </h2>
          </div>
          <div className="lg:pl-8 lg:border-l lg:border-aegisStone">
            <p className="font-inter text-aegisMuted text-base leading-relaxed">
              {subtitle}
            </p>
          </div>
        </div>

        {/* Services Grid — image cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-aegisStone">
          {services.map((service, idx) => (
            <Link
              key={service.id}
              to={service.link}
              className={`group relative block bg-white overflow-hidden cursor-pointer${services.length % 3 !== 0 && idx === services.length - 1 ? " md:col-span-2 lg:col-span-3" : ""}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={SERVICE_IMAGES[idx] ?? SERVICE_IMAGES[0]}
                  alt={service.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-aegisDark/70 via-aegisDark/20 to-transparent" />
                {/* Number overlay */}
                <span className="absolute top-3 right-4 font-cormorant font-semibold text-white/20 text-5xl leading-none select-none group-hover:text-aegisGold/40 transition-colors duration-300">
                  {NUM_LABELS[idx]}
                </span>
              </div>

              {/* Content */}
              <div className="p-7">
                {/* Icon */}
                <div className="w-10 h-10 flex items-center justify-center border border-aegisStone group-hover:border-aegisGold transition-colors duration-300 mb-5">
                  <i className={`${service.icon} text-aegisGold text-lg`} />
                </div>

                <h3 className="font-cormorant font-semibold text-aegisDark text-xl mb-3 group-hover:text-aegisGold transition-colors duration-200 leading-tight">
                  {service.title}
                </h3>

                {/* Gold divider */}
                <div className="w-8 h-0.5 bg-aegisGold/40 group-hover:w-14 group-hover:bg-aegisGold transition-all duration-300 mb-4" />

                <p className="font-inter text-aegisMuted text-sm leading-relaxed mb-5">
                  {service.description}
                </p>

                <div className="flex items-center gap-2 font-inter text-xs tracking-[0.15em] uppercase text-aegisGold">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-200" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* After-services CTA band */}
        {ctaText && ctaHref && (
          <div className="mt-0 bg-aegisDark px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-7">
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 flex items-center justify-center border border-aegisGold/30 flex-shrink-0">
                <i className="ri-building-4-line text-aegisGold text-xl" />
              </div>
              <div>
                <p className="font-inter text-xs tracking-[0.18em] uppercase text-aegisGold mb-1.5">Every Service. One Team.</p>
                <h3 className="font-cormorant font-semibold text-white text-2xl leading-snug max-w-lg">
                  All structural work self-performed by our in-house crews — zero subcontractors.
                </h3>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a
                href={COMPANY_INFO.phoneHref}
                className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-inter font-medium text-sm tracking-wide uppercase px-7 py-3.5 whitespace-nowrap cursor-pointer hover:border-aegisGold hover:text-aegisGold transition-all duration-200"
              >
                <i className="ri-phone-line" />
                {COMPANY_INFO.phone}
              </a>
              <Link
                to={ctaHref}
                className="inline-flex items-center justify-center gap-2 bg-aegisGold text-white font-inter font-semibold text-sm tracking-widest uppercase px-7 py-3.5 whitespace-nowrap cursor-pointer hover:bg-amber-700 transition-colors duration-200"
              >
                {ctaText}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
